import { useMemo } from 'react';
import {
  RateDetail,
  RegularRule,
  SpecificRule,
} from '../../models/ParkingRate.ts';
import { DayOfWeek } from '../../models/OperationHours.ts';
import {
  ALL_DOWS,
  DaySlot,
  MaxFeeSlot,
  buildRegularDayCards,
  buildSpecificDayCards,
} from '../../utils/ParkingRateCardUtils.ts';
import {
  DOW_ORDER,
  dateToLabel,
  toStartOfDayISO,
  toEndOfDayISO,
} from '../../utils/ParkingRateTime.ts';
import {
  groupsToRegularRatePeriods,
  groupsToSpecificRatePeriods,
  maxFeeEntriesToRegularPeriods,
  maxFeeEntriesToSpecificPeriods,
  draftFeeItemsToMaxFeeDetails,
  type MaxFeeRuleEntry,
} from '../../utils/ParkingRateConverters.ts';
import { type RateGroup } from './useParkingRateGroups.ts';

interface UseParkingRatePreviewParams {
  localRuleType: 'regular' | 'specific';
  startDate: Date | null;
  endDate: Date | null;
  regularComputedEndDate: string | null;
  name: string;
  groups: RateGroup<RateDetail>[];
  regularDowSubset: number[];
  specialDates: string[];
  maxFeeEntries: MaxFeeRuleEntry[];
}

export function useParkingRatePreview({
  localRuleType,
  startDate,
  endDate,
  regularComputedEndDate,
  name,
  groups,
  regularDowSubset,
  specialDates,
  maxFeeEntries,
}: UseParkingRatePreviewParams) {
  const specialColumnLabels = useMemo(
    () => specialDates.map(dateToLabel),
    [specialDates],
  );

  // ── Step 2 費率預覽資料 ──────────────────────────────────────────────────
  const previewDayCards = useMemo(() => {
    if (localRuleType !== 'regular' || !startDate || !regularComputedEndDate)
      return [];
    const provisionalRule: RegularRule = {
      name,
      effectivePeriod: {
        startDateTime: toStartOfDayISO(startDate),
        endDateTime: toEndOfDayISO(regularComputedEndDate),
      },
      ratePeriods: groupsToRegularRatePeriods(groups, regularDowSubset),
      maximumFeeRules: maxFeeEntriesToRegularPeriods(maxFeeEntries),
      additionalRules: { entryBufferTime: null, isResetEveryDay: false },
      description: '',
    };
    return buildRegularDayCards(provisionalRule);
  }, [
    localRuleType,
    startDate,
    regularComputedEndDate,
    name,
    groups,
    regularDowSubset,
    maxFeeEntries,
  ]);

  const previewDateCards = useMemo(() => {
    if (localRuleType !== 'specific' || !startDate || !endDate) return [];
    const provisionalRule: SpecificRule = {
      name,
      effectivePeriod: {
        startDateTime: toStartOfDayISO(startDate),
        endDateTime: toEndOfDayISO(endDate),
      },
      ratePeriods: groupsToSpecificRatePeriods(groups, specialDates),
      maximumFeeRules: maxFeeEntriesToSpecificPeriods(
        maxFeeEntries,
        specialDates,
      ),
      additionalRules: { entryBufferTime: null, isResetEveryDay: false },
      description: '',
    };
    return buildSpecificDayCards(provisionalRule);
  }, [
    localRuleType,
    startDate,
    endDate,
    name,
    groups,
    specialDates,
    maxFeeEntries,
  ]);

  // ── Step 2 專用預覽：純粹依照 maxFeeEntries 分組，不受 ratePeriods 影響 ──

  const previewMaxFeeDayCards = useMemo(() => {
    if (localRuleType !== 'regular') return [];
    const cards: {
      dows: DayOfWeek[];
      slots: DaySlot[];
      maxFeeSlots: MaxFeeSlot[];
    }[] = [];
    maxFeeEntries
      .filter((e) => e.checkedDays.length > 0 && e.feeItems.length > 0)
      .forEach((e) => {
        const maxFeeDetail = draftFeeItemsToMaxFeeDetails(e.feeItems);
        const fpMap = new Map<
          string,
          {
            dayIndices: number[];
            effectiveSlots: { startTime: string; endTime: string }[];
          }
        >();
        e.checkedDays.forEach((dayIdx) => {
          const slots = e.daySlots[dayIdx] ?? [];
          const effectiveSlots =
            slots.length > 0
              ? slots.map((s) => ({
                  startTime: s.startTime,
                  endTime: s.endTime,
                }))
              : [{ startTime: '00:00', endTime: '23:59' }];
          const fp = effectiveSlots
            .map((s) => `${s.startTime}|${s.endTime}`)
            .join(';');
          if (!fpMap.has(fp)) fpMap.set(fp, { dayIndices: [], effectiveSlots });
          fpMap.get(fp)!.dayIndices.push(dayIdx);
        });
        fpMap.forEach(({ dayIndices, effectiveSlots }) => {
          const sortedIndices = [...dayIndices].sort((a, b) => a - b);
          cards.push({
            dows: sortedIndices.map((d) => DOW_ORDER[d]) as DayOfWeek[],
            slots: [],
            maxFeeSlots: effectiveSlots.map((s) => ({
              startTime: s.startTime,
              endTime: s.endTime,
              maximumFee: maxFeeDetail,
            })),
          });
        });
      });
    return cards.sort(
      (a, b) => ALL_DOWS.indexOf(a.dows[0]) - ALL_DOWS.indexOf(b.dows[0]),
    );
  }, [localRuleType, maxFeeEntries]);

  const previewMaxFeeDateCards = useMemo(() => {
    if (localRuleType !== 'specific') return [];
    const cards: {
      dates: string[];
      slots: DaySlot[];
      maxFeeSlots: MaxFeeSlot[];
    }[] = [];
    maxFeeEntries
      .filter((e) => e.checkedDays.length > 0 && e.feeItems.length > 0)
      .forEach((e) => {
        const maxFeeDetail = draftFeeItemsToMaxFeeDetails(e.feeItems);
        const validIndices = e.checkedDays.filter(
          (d) => d < specialDates.length,
        );
        const fpMap = new Map<
          string,
          {
            dateIndices: number[];
            effectiveSlots: { startTime: string; endTime: string }[];
          }
        >();
        validIndices.forEach((dateIdx) => {
          const slots = e.daySlots[dateIdx] ?? [];
          const effectiveSlots =
            slots.length > 0
              ? slots.map((s) => ({
                  startTime: s.startTime,
                  endTime: s.endTime,
                }))
              : [{ startTime: '00:00', endTime: '23:59' }];
          const fp = effectiveSlots
            .map((s) => `${s.startTime}|${s.endTime}`)
            .join(';');
          if (!fpMap.has(fp))
            fpMap.set(fp, { dateIndices: [], effectiveSlots });
          fpMap.get(fp)!.dateIndices.push(dateIdx);
        });
        fpMap.forEach(({ dateIndices, effectiveSlots }) => {
          const sortedIndices = [...dateIndices].sort((a, b) => a - b);
          cards.push({
            dates: sortedIndices.map((d) => specialDates[d]),
            slots: [],
            maxFeeSlots: effectiveSlots.map((s) => ({
              startTime: s.startTime,
              endTime: s.endTime,
              maximumFee: maxFeeDetail,
            })),
          });
        });
      });
    return cards.sort((a, b) => a.dates[0].localeCompare(b.dates[0]));
  }, [localRuleType, maxFeeEntries, specialDates]);

  return {
    specialColumnLabels,
    previewDayCards,
    previewDateCards,
    previewMaxFeeDayCards,
    previewMaxFeeDateCards,
  };
}
